import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";
import HighchartsMore from "highcharts/highcharts-more";
import HighchartsHeatmap from "highcharts/modules/heatmap";
import { blue, grey, red } from "@ant-design/colors";
import { useRef } from "react";
import { useValue } from "src/utils/useValue";
import useTranslate from "src/utils/useTranslate";
import Boost from 'highcharts/modules/boost';
import Plot from "react-plotly.js";

Boost(Highcharts);

HighchartsMore(Highcharts);
HighchartsHeatmap(Highcharts);

const heatmapOptionsHighcharts = (
  performance: any,
  model: any,
  data: any,
  getEUValue: any,
  t: any
): Highcharts.Options => {
  const categories = performance[model]["CATEGORY_ORDER"] || [];
  const result = performance[model]["CLASSIFICATION_RESULT"] || [];

  const dataHeatmap: any[] = [];
  for (let row = 0; row < result.length; row++) {
    for (let col = 0; col < result[row].length; col++) {
      dataHeatmap.push([row, col, result[row][col]]);
    }
  }

  return {
    chart: {
      type: "heatmap",
      zoomType: "xy",
      height: 600,
      plotBorderWidth: 1,
      reflow: true
    },
    credits: { enabled: false },
    legend: { enabled: false },
    boost: {
      enabled: true,
      useGPUTranslations: true,
      usePreallocated: true,
    },
    xAxis: {
      gridLineWidth: 0,
      categories: categories,
      opposite: true,
      title: {
        text: "Predicted",
      },
    },
    tooltip: { enabled: false },
    yAxis: {
      gridLineWidth: 0,
      categories: categories,
      reversed: true,
      title: {
        text: "Actual",
      },
    },
    title: {
      text: `<b>${model}</b>`,
      align: "left",
    },
    colorAxis: {
      min: 0,
      minColor: "#FFFFFF",
      maxColor: "#2caffe",
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 1280,
          },
          chartOptions: {
            chart: {
              height: 300,
            },
          },
        },
      ],
    },
    series: [
      {
        type: "heatmap",
        data: dataHeatmap,
        borderWidth: 1,
        dataLabels: {
          enabled: true,
          formatter: function (this: any) {
            return `<b>${getEUValue(this.point.value)}</b>`;
          },
          color: "#000000",
          style: {
            fontSize: "1rem",
          },
        },
      },
    ],
  };
};

const scatterOptionsHighcharts = (
  performance: any,
  model: any,
  data: any,
  getEUValue: any,
  t: any,
): Highcharts.Options => {
  const r2 = performance[model]["R2 Score"] || [];
  const mape = performance[model]["MAPE"] || [];
  const actual = data["actual"] || [];
  const predicted = data["predicted"][model]["central"] || [];
  const upper = data["predicted"][model]["upper_limit"] || [];
  const lower = data["predicted"][model]["lower_limit"] || [];
  const exps = data["formulation_display_id"] || [];

  return {
    chart: {
      zoomType: "xy",
      height: 600,
      reflow: true,
    },
    credits: { enabled: false },
    legend: { enabled: false },
    boost: {
      enabled: true,
      useGPUTranslations: true,
      usePreallocated: true,
    },
    xAxis: {
      gridLineWidth: 0,
      title: {
        text: `<b>${t("compareGraph.actual")}</b>`
      },
      labels: {
        formatter: function (this: any) {
          const point = getEUValue(this.value)
          return point;
        }
      },
    },
    yAxis: {
      gridLineWidth: 0,
      title: {
        text: `<b>${t("common.predicted")}</b>`
      },
      labels: {
        formatter: function (this: any) {
          const point = getEUValue(this.value)
          return point;
        }
      },
    },
    title: {
      text: `<b>${model}</b>`,
      align: "left",
    },
    subtitle: {
      text: `<b>${t("aiEngine.modelAnalysis.r2Score")}: ${getEUValue(r2)}</b> <br/> <b>MAPE: ${getEUValue(mape)}%</b>`,
      align: "left",
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 1280,
          },
          chartOptions: {
            chart: {
              height: 300,
            },
          },
        },
      ],
    },
    series: [
      {
        type: "errorbar",
        pointWidth: 15,
        data: actual.map((a: any, i: any) => [a, lower[i], upper[i]]),
        color: grey[2],
        enableMouseTracking: true,
        crisp: false,
        tooltip: {
          pointFormatter: function (this: any) {
            return `<b>${getEUValue(this.low)} - ${getEUValue(this.high)}</b>`
          },
        },
      },
      {
        type: "line",
        data: actual.map((a: any) => [a, a]),
        lineWidth: 3,
        color: red[5],
        enableMouseTracking: false,
        dashStyle: "Dot",
        crisp: false,
        marker: {
          enabled: false,
        },
      },
      {
        type: "scatter",
        data: actual.map((a: any, i: any) => ({ x: a, y: predicted[i], exps: exps[i] })),
        tooltip: {
          pointFormatter: function (this: any) {
            return `<b>${t("common.experiment")}: ${this?.exps}</b><br><b>${t("aiEngine.modelAnalysis.predictedValue")}: ${getEUValue(this.y)}</b><br><b>${t("aiEngine.modelAnalysis.actualValue")}: ${getEUValue(this.x)}</b><br>`
          },
        },
        color: blue[5],
        marker: {
          symbol: "circle",
          radius: 3,
        },
        crisp: false,
      },
    ],
  };
};

const getChartOptionsHighcharts = (type: string, performance: any, model: any, data: any, getEUValue: any, t: any) => {
  if (type === 'REGRESSOR') return scatterOptionsHighcharts(performance, model, data, getEUValue, t)
  if (type === 'CLASSIFIER') return heatmapOptionsHighcharts(performance, model, data, getEUValue, t)
}

const scatterOptionsPlotly = (performance: any, model: any, data: any, getEUValue: any, t: any, getDecimalSeparator: any): any => {
  const r2: string[] = performance[model]['R2 Score'] || []
  const mape: string[] = performance[model]['MAPE'] || []
  const actual = data['actual'] || []
  const predicted: number[] = data['predicted'][model]['central'] || []
  const upper: number[] = data['predicted'][model]['upper_limit'] || []
  const lower: number[] = data['predicted'][model]['lower_limit'] || []
  const exps: string[] = data['formulation_display_id'] || []

  return {
    data: [
      {
        type: 'scattergl',
        mode: 'markers',
        x: actual,
        y: predicted,
        text: predicted.map(
          (_, i) =>
            `<b>${t('common.experiment')}: ${exps[i] ?? '-'}</b><br><b>${t('aiEngine.modelAnalysis.predictedValue')}: ${predicted[i]} (±${(upper[i] - predicted[i]).toFixed(3)})</b><br><b>${t('aiEngine.modelAnalysis.actualValue')}: ${actual[i]}</b>`
        ),
        hoverinfo: 'text',
        line: {
          color: blue[5],
          width: 1,
          dash: 'solid'
        },
        marker: {
          size: 6
        },
        error_y: {
          type: 'data',
          symmetric: false,
          array: upper.map((u, i) => u - predicted[i]),
          arrayminus: lower.map((l, i) => predicted[i] - l),
          color: grey[2],
          thickness: 1,
          width: 2,
          visible: true
        },
        showlegend: false
      },
      {
        type: 'scattergl',
        x: actual,
        y: actual,
        line: {
          color: red[5],
          width: 1,
          // dash: 'solid'
        },
        hoverinfo: 'none',
        showlegend: false,
        mode: 'lines'
      }
    ],
    layout: {
      // height: 450,
      // width: 520,
      autosize: true,
      title: {
        text: `<span style="text-align: left;">
        <b>${model}</b>
        <br>
        <span style="font-size: 0.8rem; color: #555;"><b>${t('aiEngine.modelAnalysis.r2Score')}: ${getEUValue(r2)}</b></span>
        <br>
        <span style="font-size: 0.8rem; color: #555;"><b>MAPE: ${getEUValue(mape)}%</b></span>
        </span>`,
        font: {
          size: 18,
          family: 'Arial',
          weight: 'bold'
        }
      },
      dragmode: false,
      hovermode: 'closest',
      hoverlabel: {
        bordercolor: 'black',
        bgcolor: 'black',
        font: {
          color: 'white',
          size: 12,
          family: 'Arial'
        }
      },
      xaxis: {
        title: {
          text: `<b>${t('compareGraph.actual')}</b>`
        },
        showgrid: false,
        zeroline: false,

        ticks: 'outside',
        tickcolor: 'black',
        ticklen: 5,
        tickwidth: 1,

        showline: true,
        linecolor: 'black',
        linewidth: 1
      },
      yaxis: {
        title: {
          text: `<b>${t('common.predicted')}</b>`
        },
        showgrid: false,
        zeroline: false,

        ticks: 'none',

        showline: false,
        linecolor: 'black',
        linewidth: 1
      },
      separators: getDecimalSeparator(),
    },
    config: {
      displayModeBar: false,
      scrollZoom: false,
      responsive: true
    }
  }
}

const getChartOptionsPlotly = (type: string, performance: any, model: any, data: any, getEUValue: any, t: any, getDecimalSeparator: any) => {
  if (type === 'REGRESSOR') return scatterOptionsPlotly(performance, model, data, getEUValue, t, getDecimalSeparator)
  if (type === 'CLASSIFIER') return {}
}

export default function PerformanceScatterWrapper({
  performance,
  data,
  type,
}: any) {

  const { getValue: getEUValue, getDecimalSeparator } = useValue()
  const [t] = useTranslate()
  const IndividualChart = ({ model, index }: any) => {

    const chartRef = useRef(null);

    return (
      <div
        key={model + index}
        style={{
          border: '1px solid #ccc',
          width: '100%'
        }}
      >
        {type === 'CLASSIFIER' ? (
          <HighchartsReact
            highcharts={Highcharts}
            options={getChartOptionsHighcharts(type, performance, model, data, getEUValue, t)}
            ref={chartRef}
          />
        ) : (
          <Plot {...getChartOptionsPlotly(type, performance, model, data, getEUValue, t, getDecimalSeparator)} style={{
            width: '100%',
            height: '100%'
          }} />
        )}
      </div>
    )
  }

  return (
    <div style={{ display: "flex", gap: 10, width: "100%" }}>
      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0,1fr))",
          gap: 10,
        }}
      >
        {Object.keys(performance).map((model: any, index: number) => {
          return (
            <IndividualChart model={model} index={index} key={model} />
          );
        })}
      </div>
    </div>
  );
}
